// AIT-639 — ChatGPT Ads measurement pixel for the frontend/ app. Same contract
// as frontend/src/lib/firstpromoter.ts:
//   - SSR-safe (no-op when window is undefined)
//   - Idempotent
//   - Local-dev safe (no-op when the pixel id is empty)
//
// The SDK (`oaiq.min.js`) drains the `window.oaiq.q` queue when it loads, so
// the inline queue shim AND the init call must run BEFORE the script tag is
// appended. Once loaded it reads the `oppref` click id the landing page
// (web/) captured into the first-party `__obref` cookie, so a click on
// hookmyapp.com and a signup on app.hookmyapp.com attribute to the same ad.
//
// Docs: https://developers.openai.com/ads/measurement-pixel

type OaiqQueue = { (...args: unknown[]): void; q?: unknown[][] };

declare global {
  interface Window {
    oaiq?: OaiqQueue;
  }
}

let initialized = false;

// Routes whose URL carries a credential (handoff JWT in the query, Meta/WorkOS
// tokens or codes in the hash/query, card-link token). The SDK is loaded
// lazily by the registration mount and never while one of these is the
// current path (same shape as the /card gate in main.tsx; Codex review).
// /invite/* is deliberately NOT here: the slug is a shareable onboarding
// link, and /invite/connect is the affiliate signup's returnTo.
const CREDENTIAL_PATH_PREFIXES = [
  '/card',
  '/signup',
  '/sandbox',
  '/meta/callback',
  '/cli/callback',
  '/cli/billing/callback',
  '/connect/callback',
];

export function isCredentialBearingPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '');
  return CREDENTIAL_PATH_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + '/'));
}

export function initOpenAIPixel(): void {
  if (typeof window === 'undefined' || initialized) return;
  if (isCredentialBearingPath(window.location.pathname)) return;
  const pixelId = import.meta.env.VITE_OPENAI_PIXEL_ID;
  if (!pixelId) return;

  if (!window.oaiq) {
    const oaiq: OaiqQueue = (...args: unknown[]) => {
      oaiq.q = oaiq.q || [];
      oaiq.q.push(args);
    };
    window.oaiq = oaiq;
  }
  window.oaiq('init', { pixelId });

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
  document.head.appendChild(script);

  initialized = true;
}

const STORAGE_PREFIX = 'oaiq_registered:';

// Storage can throw (SecurityError under strict browser policy). Treat it as
// "no marker": better a rare duplicate than a lost conversion (Codex review).
function storageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function storageSet(key: string): void {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    /* ponytail: no fallback store; duplicates are deduped server-side by event_id */
  }
}

async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Fire `registration_completed` exactly once for a fresh signup. Returns
 * true when the event was queued, false when gated (pixel off, returning
 * user, already sent). `isFreshSignup` is decided server-side by /auth/me.
 * Hashing follows the pixel's normalization rules (email trimmed +
 * lowercased; external id trimmed). Only hashes leave the browser: the
 * dedup `event_id` reuses the external-id hash rather than the raw user id.
 */
export async function trackRegistration(user: {
  id: string;
  email: string | null;
  isFreshSignup?: boolean;
}): Promise<boolean> {
  if (typeof window === 'undefined' || !window.oaiq) return false;
  if (!user.isFreshSignup) return false;
  const key = STORAGE_PREFIX + user.id;
  if (storageGet(key)) return false;

  const [external_id_sha256, email_sha256] = await Promise.all([
    sha256Hex(user.id.trim()),
    user.email ? sha256Hex(user.email.trim().toLowerCase()) : Promise.resolve(undefined),
  ]);
  window.oaiq('init', { user: { external_id_sha256, ...(email_sha256 ? { email_sha256 } : {}) } });
  window.oaiq('measure', 'registration_completed', { type: 'customer_action' }, { event_id: external_id_sha256 });
  // Mark only after the event is queued: a hashing failure above must not
  // burn the one-shot for this user (Codex review).
  storageSet(key);
  return true;
}
